import { api } from "@/lib/api";
import {
  HotelRoomDto,
  hotelRoomDtoListSchema,
  HotelRoomDtoSchema,
  hotelRoomFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export function getHotelRoomsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["hotel-rooms", id],
    queryFn: async () => {
      const result: Array<HotelRoomDto> = [];
      let page = 0;
      let hasNextPage = true;
      while (hasNextPage) {
        page += 1;
        const res = await api.getWithCache("/api/hotel-rooms", hotelRoomDtoListSchema, {
          query: {
            page: page.toString(),
          },
          filters: { HotelId: Number.parseInt(id) },
          projection: ["Hotel"],
        });
        result.push(...res.Data);
        hasNextPage = res.PageInfo.HasNextPage;
      }
      return result.map(hotelRoomFromDto);
    },
  });
}

export function useGetHotelRooms<TERROR extends Error>(
  id: string,
  opts?: {
    onError?: (error: TERROR) => void;
    enabled?: boolean;
  },
) {
  const { toast } = useToast();

  const q = useQuery({
    ...getHotelRoomsQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getHotelRoomsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export const getAllHotelRoomsQueryOptions = queryOptions({
  queryKey: ["all-hotel-rooms"],
  queryFn: async () => {
    const result: Array<HotelRoomDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/hotel-rooms", hotelRoomDtoListSchema, {
        query: {
          page: page.toString(),
        },
        projection: ["Hotel"],
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(hotelRoomFromDto);
  },
});

export function useGetAllHotelRooms(opts?: { enabled?: boolean }) {
  return useQuery({
    ...getAllHotelRoomsQueryOptions,
    enabled: opts?.enabled,
  });
}

export function getHotelRoomQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["hotel-room", id],
    queryFn: () =>
      api
        .get("/api/hotel-rooms/$id", HotelRoomDtoSchema, {
          pathReplace: { $id: id },
          projection: ["Hotel"],
        })
        .then(hotelRoomFromDto),
  });
}

export function useGetHotelRoom<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getHotelRoomQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getHotelRoomDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
